import React, { memo, useCallback, useEffect, useRef, useState } from 'react';

export interface PaginatedListProps<T> {
  /**
   * The items to be rendered
   */
  readonly items: readonly T[];

  /**
   * Function to render each item
   */
  readonly renderItem: (item: T, index: number) => React.ReactNode;

  /**
   * Function to load more items when the button is clicked
   */
  readonly loadMore: () => void;

  /**
   * Whether there are more items to load
   */
  readonly hasMore: boolean;

  /**
   * Element to show while loading
   */
  readonly loader?: React.ReactNode;

  /**
   * Element to show when all items are loaded
   */
  readonly endMessage?: React.ReactNode;

  /**
   * Element to show when no items are available
   */
  readonly emptyMessage?: React.ReactNode;

  /**
   * Custom class name for the container
   */
  readonly className?: string;

  /**
   * Custom class name for the "Load More" button
   */
  readonly buttonClassName?: string;

  /**
   * Custom text for the "Load More" button
   */
  readonly buttonText?: string;

  /**
   * Whether items are currently being loaded
   */
  readonly isLoading?: boolean;

  /**
   * Optional ID for the container element
   */
  readonly id?: string;

  /**
   * Optional key function to generate keys for items
   */
  readonly keyExtractor?: (item: T, index: number) => string | number;

  /**
   * Optional custom render function for the load more button
   */
  readonly renderLoadMoreButton?: (handleLoadMore: () => void) => React.ReactNode;
}

/**
 * A component that displays a list of items with a "Load More" button to fetch additional items.
 *
 * @component
 * @example
 * const ExampleComponent = () => {
 *   const [items, setItems] = useState<Item[]>([]);
 *   const [hasMore, setHasMore] = useState(true);
 *   const [isLoading, setIsLoading] = useState(false);
 *   const [page, setPage] = useState(1);
 *
 *   const fetchMoreData = async () => {
 *     setIsLoading(true);
 *     const newItems = await fetchItems(page);
 *     setItems(prev => [...prev, ...newItems]);
 *     setPage(prev => prev + 1);
 *     setHasMore(newItems.length > 0);
 *     setIsLoading(false);
 *   };
 *
 *   useEffect(() => {
 *     fetchMoreData();
 *   }, []);
 *
 *   return (
 *     <LoadMore<Item>
 *       items={items}
 *       renderItem={(item) => <ItemCard item={item} />}
 *       loadMore={fetchMoreData}
 *       hasMore={hasMore}
 *       isLoading={isLoading}
 *       loader={<Spinner />}
 *       endMessage={<p>No more items to load.</p>}
 *       emptyMessage={<p>No items available.</p>}
 *       keyExtractor={(item) => item.id}
 *     />
 *   );
 * };
 */
function PaginatedListComponent<T>({
  items,
  renderItem,
  loadMore,
  hasMore,
  loader,
  endMessage,
  emptyMessage,
  className = 'load-more-container',
  isLoading = false,
  id,
  keyExtractor,
  renderLoadMoreButton,
}: PaginatedListProps<T>): React.ReactElement {
  // Track previous items for comparison
  const prevItemsRef = useRef<readonly T[]>([]);
  const loadMoreClicksRef = useRef(0);

  // Add state to track if a load operation is in progress
  const [loadMoreTriggered, setLoadMoreTriggered] = useState<boolean>(false);

  // Reset the load more triggered flag when isLoading changes from true to false
  useEffect(() => {
    if (!isLoading && loadMoreTriggered) {
      setLoadMoreTriggered(false);
    }
  }, [isLoading, loadMoreTriggered]);

  // Compare with previous items on each render
  useEffect(() => {
    if (items !== prevItemsRef.current) {
      prevItemsRef.current = items;
    }
  }, [items, loadMoreTriggered]);

  // Memoize the loadMore callback to prevent unnecessary renders
  const handleLoadMore = useCallback(() => {
    if (!isLoading && !loadMoreTriggered) {
      loadMoreClicksRef.current += 1;

      // Set our flag before calling loadMore
      setLoadMoreTriggered(true);
      loadMore();
    }
  }, [loadMore, isLoading, items.length, loadMoreTriggered]);

  // Early return for empty state
  if (items.length === 0) {
    if (isLoading && loader) {
      return (
        <div className={className} id={id}>
          {loader}
        </div>
      );
    }
    return (
      <div className={className} id={id}>
        {emptyMessage}
      </div>
    );
  }

  // Render list items with proper keys
  const renderedItems = items.map((item, index) => {
    const key = keyExtractor ? keyExtractor(item, index) : index;
    return <React.Fragment key={key}>{renderItem(item, index)}</React.Fragment>;
  });

  return (
    <div className={className} id={id}>
      <div className="load-more-items">{renderedItems}</div>

      {isLoading && loader}

      {hasMore && !isLoading && (
        <div className="load-more-button-container">
          {renderLoadMoreButton ? (
            renderLoadMoreButton(handleLoadMore)
          ) : (
            <div className="p-3 text-center selectable fw-bold" onClick={handleLoadMore} data-cy="list-load-more">
              LOAD MORE
            </div>
          )}
        </div>
      )}

      {!hasMore && items.length > 0 && endMessage}
    </div>
  );
}

// Use memo to prevent unnecessary re-renders
const PaginatedList = memo(PaginatedListComponent) as typeof PaginatedListComponent;

export default PaginatedList;
